import { gql, useLazyQuery, useMutation, useQuery } from '@apollo/client';
import { TRANSLATED_STRING_FIELDS } from '@assets/i18n/config';
import { type CustomField, FETCH_CUSTOM_FIELDS } from '@interfaces/customField';
import type { EnabledJurisdictions } from '@interfaces/organization';
import { EntityTypes, TrustTypes } from '@interfaces/user';
import { SubStepTypes } from '@interfaces/workflow';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { getWorkflowCompletionHeaders } from '@ovComponents/4-module/workflowCompletion/workflowCompletionHeaders';
import { UserContext, usePermission } from '@providers/userContextProvider';
import { formatPhoneNumber } from '@utils/common';
import { modifyUpdatingCustomFields } from '@utils/modifyUpdatingCustomFields';
import { useSnackbar } from 'hephaestus/web';
import { isEmpty } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getEnabledCustomFields } from '../utils';
import { NonIndividualInformationVisual } from './nonIndividualInformation.visual';

const FETCH_USER = (derivedCustomFields?: string[]) => gql`
  query fetchUserNonIndividualInfo($userId: SafeString!) {
    fetchUser(input: { userId: $userId }) {
      user {
        id: stringId
        entityName
        type
        typeOfTrust
        primaryEmail
        phone
        companyType
        registrationNumber
        establishedDate
        countryOfRegistration
        language
        preferredMethodOfCommunication
        physicalAddress { city province streetName postal unitNumber houseNumber neighborhood country }
        mailingAddress { city province streetName postal unitNumber houseNumber neighborhood country }
        alternateAddress { line1 line2 city country postal jurisdiction }
        ${
          derivedCustomFields
            ? `customFields(keys: ${JSON.stringify(derivedCustomFields)}) {
          key
          value
          customField { id translatedName { ${TRANSLATED_STRING_FIELDS} } translatedDescription{ ${TRANSLATED_STRING_FIELDS} } type format}
          selectedOptions { value displayText { ${TRANSLATED_STRING_FIELDS} } }
        }`
            : ''
        }
        organization {
          jurisdictions { all only }
        }
      }
    }
  }
`;

const UPDATE_USER = gql`
  mutation updateUser($input: UpdateUserInput!) {
    updateUser(input: $input) {
      user {
        id
      }
    }
  }
`;

export const NonIndividualInformation = ({
  options,
  userId,
  onNext,
  stepLoading,
  grid = false,
  updateMode = false,
  workflowCompletion,
}: {
  options: any;
  userId: string;
  onNext: () => void;
  stepLoading: boolean;
  grid?: boolean;
  updateMode?: boolean;
  workflowCompletion?: any;
}) => {
  const { t } = useTranslation(['shared']);
  const { activeOrganization } = useContext(UserContext);
  const canReadCustomFields = usePermission('read:custom_fields');
  const { enqueueSnackbar } = useSnackbar();
  const [derivedCustomFields, setDerivedCustomFields] =
    useState<CustomField[]>();
  const [derivedCustomFieldsKeys, setDerivedCustomFieldsKeys] =
    useState<string[]>();
  const [hasMailingAddress, setHasMailingAddress] = useState(false);
  const [hasAlternateAddress, setHasAlternateAddress] = useState(false);
  const enabledCustomFields = getEnabledCustomFields(options);
  const [userData, setUserData] = useState<any>({
    id: '',
    entityName: '',
    type: '',
    typeOfTrust: '',
    primaryEmail: '',
    phone: '',
    companyType: '',
    registrationNumber: '',
    establishedDate: '',
    countryOfRegistration: '',
    language: '',
    preferredMethodOfCommunication: '',
    physicalAddress: {
      city: '',
      province: '',
      streetName: '',
      postal: '',
      unitNumber: '',
      houseNumber: '',
      neighborhood: '',
      country: '',
    },
    mailingAddress: undefined,
    alternateAddress: undefined,
    customFields: [],
  });
  const [applicableJurisdictions, setApplicableJurisdictions] =
    useState<EnabledJurisdictions>({ all: true });

  const { loading: customFieldLoading } = useQuery(FETCH_CUSTOM_FIELDS, {
    variables: {
      input: {
        filter: {
          workflowStep: SubStepTypes.NON_INDIVIDUAL_INFORMATION,
          organizationId: activeOrganization?.id ?? undefined,
          keys: !isEmpty(enabledCustomFields) ? enabledCustomFields : undefined,
        },
        pagination: {
          perPage: 100,
        },
      },
    },
    fetchPolicy: 'no-cache',
    skip: !canReadCustomFields || enabledCustomFields.length === 0,
    onCompleted: async (res: any) => {
      const customFieldKeys = res?.fetchCustomFields?.customFields?.map(
        (a: any) => a.key,
      );
      setDerivedCustomFields(res?.fetchCustomFields?.customFields);
      await fetchUser({
        variables: {
          userId,
        },
        query: customFieldKeys ? FETCH_USER(customFieldKeys) : undefined,
      });
    },
  });

  const [fetchUser, { data, loading }] = useLazyQuery(FETCH_USER(), {
    variables: { userId },
    fetchPolicy: 'no-cache',
  });

  const { data: defaultdata } = useQuery(FETCH_USER(), {
    variables: { userId },
    fetchPolicy: 'no-cache',
    skip: canReadCustomFields && enabledCustomFields.length !== 0,
  });

  useEffect(() => {
    if (derivedCustomFields) {
      const fields = derivedCustomFields.map((a) => a.key);
      setDerivedCustomFieldsKeys(fields);
    }
  }, [derivedCustomFields]);

  const [updateUser, { loading: updateUserLoading }] = useMutation(
    UPDATE_USER,
    {
      context: {
        headers: getWorkflowCompletionHeaders(workflowCompletion),
      },
      onCompleted: () => {
        if (updateMode) {
          enqueueSnackbar(t('profileDetailsUpdated'), { variant: 'success' });
        }
        onNext();
      },
      variables: {
        input: {
          // removing id from userData because graphql doesn't support id in update mutation
          ...Object.fromEntries(
            Object.entries(userData).filter(([key]) => !['id'].includes(key)),
          ),
          physicalAddress: isEmpty(userData.physicalAddress)
            ? undefined
            : userData.physicalAddress,
          mailingAddress: isEmpty(userData.mailingAddress)
            ? null
            : userData.mailingAddress,
          alternateAddress: isEmpty(userData.alternateAddress)
            ? null
            : userData.alternateAddress,
          customFields: modifyUpdatingCustomFields(
            userData?.customFields,
            derivedCustomFields ?? [],
            derivedCustomFieldsKeys,
          ),
          typeOfTrust:
            userData.type === EntityTypes.TRUST
              ? userData.typeOfTrust
              : undefined,
          userId,
        },
      },
    },
  );

  const defaultTypeOfTrust =
    options?.typeOfTrust?.enabled &&
    options?.typeOfTrust?.required === FieldRequiredType.ERROR
      ? 'COMPLEX'
      : undefined;

  useEffect(() => {
    if (data) {
      setUserData({
        id: data?.fetchUser?.user?.id || '',
        entityName: data.fetchUser.user.entityName || undefined,
        type: data.fetchUser.user.type || undefined,
        typeOfTrust: data.fetchUser.user.typeOfTrust || defaultTypeOfTrust,
        primaryEmail: data.fetchUser.user.primaryEmail || undefined,
        phone: data.fetchUser.user.phone || undefined,
        companyType: data.fetchUser.user.companyType || undefined,
        registrationNumber: data.fetchUser.user.registrationNumber || undefined,
        establishedDate: data.fetchUser.user.establishedDate || undefined,
        countryOfRegistration:
          data.fetchUser.user.countryOfRegistration || undefined,
        language: data.fetchUser.user.language || undefined,
        preferredMethodOfCommunication:
          data.fetchUser.user.preferredMethodOfCommunication || undefined,
        physicalAddress: data.fetchUser.user.physicalAddress
          ? {
              city: data.fetchUser.user.physicalAddress?.city || undefined,
              province:
                data.fetchUser.user.physicalAddress?.province || undefined,
              streetName:
                data.fetchUser.user.physicalAddress?.streetName || undefined,
              postal: data.fetchUser.user.physicalAddress?.postal || undefined,
              unitNumber:
                data.fetchUser.user.physicalAddress?.unitNumber || undefined,
              houseNumber:
                data.fetchUser.user.physicalAddress?.houseNumber || undefined,
              neighborhood:
                data.fetchUser.user.physicalAddress?.neighborhood || undefined,
              country:
                data.fetchUser.user.physicalAddress?.country || undefined,
            }
          : {},
        mailingAddress: data.fetchUser.user.mailingAddress
          ? {
              city: data.fetchUser.user.mailingAddress?.city || undefined,
              province:
                data.fetchUser.user.mailingAddress?.province || undefined,
              streetName:
                data.fetchUser.user.mailingAddress?.streetName || undefined,
              postal: data.fetchUser.user.mailingAddress?.postal || undefined,
              unitNumber:
                data.fetchUser.user.mailingAddress?.unitNumber || undefined,
              houseNumber:
                data.fetchUser.user.mailingAddress?.houseNumber || undefined,
              neighborhood:
                data.fetchUser.user.mailingAddress?.neighborhood || undefined,
              country: data.fetchUser.user.mailingAddress?.country || undefined,
            }
          : {},
        alternateAddress: data.fetchUser.user.alternateAddress
          ? {
              line1: data.fetchUser.user.alternateAddress?.line1 || undefined,
              line2: data.fetchUser.user.alternateAddress?.line2 || undefined,
              city: data.fetchUser.user.alternateAddress?.city || undefined,
              country:
                data.fetchUser.user.alternateAddress?.country || undefined,
              postal: data.fetchUser.user.alternateAddress?.postal || undefined,
              jurisdiction:
                data.fetchUser.user.alternateAddress?.jurisdiction || undefined,
            }
          : {},
        customFields: data.fetchUser?.user?.customFields || [],
      });
      setApplicableJurisdictions(
        data.fetchUser.user.organization.jurisdictions,
      );
      setHasMailingAddress(
        Boolean(data.fetchUser.user.mailingAddress) &&
          !isEmpty(data.fetchUser.user.mailingAddress),
      );
      setHasAlternateAddress(
        Boolean(data.fetchUser.user.alternateAddress) &&
          !isEmpty(data.fetchUser.user.alternateAddress),
      );
    }
    if (defaultdata) {
      setUserData({
        id: defaultdata?.fetchUser?.user?.id || '',
        entityName: defaultdata.fetchUser.user.entityName || undefined,
        type: defaultdata.fetchUser.user.type || undefined,
        typeOfTrust:
          defaultdata.fetchUser.user.typeOfTrust || defaultTypeOfTrust,
        primaryEmail: defaultdata.fetchUser.user.primaryEmail || undefined,
        phone: formatPhoneNumber(defaultdata.fetchUser.user.phone) || undefined,
        companyType: defaultdata.fetchUser.user.companyType || undefined,
        registrationNumber:
          defaultdata.fetchUser.user.registrationNumber || undefined,
        establishedDate:
          defaultdata.fetchUser.user.establishedDate || undefined,
        countryOfRegistration:
          defaultdata.fetchUser.user.countryOfRegistration || undefined,
        language: defaultdata.fetchUser.user.language || undefined,
        preferredMethodOfCommunication:
          defaultdata.fetchUser.user.preferredMethodOfCommunication ||
          undefined,
        physicalAddress: defaultdata.fetchUser.user.physicalAddress
          ? {
              city:
                defaultdata.fetchUser.user.physicalAddress?.city || undefined,
              province:
                defaultdata.fetchUser.user.physicalAddress?.province ||
                undefined,
              streetName:
                defaultdata.fetchUser.user.physicalAddress?.streetName ||
                undefined,
              postal:
                defaultdata.fetchUser.user.physicalAddress?.postal || undefined,
              unitNumber:
                defaultdata.fetchUser.user.physicalAddress?.unitNumber ||
                undefined,
              houseNumber:
                defaultdata.fetchUser.user.physicalAddress?.houseNumber ||
                undefined,
              neighborhood:
                defaultdata.fetchUser.user.physicalAddress?.neighborhood ||
                undefined,
              country:
                defaultdata.fetchUser.user.physicalAddress?.country ||
                undefined,
            }
          : {},
        mailingAddress: defaultdata.fetchUser.user.mailingAddress
          ? {
              city:
                defaultdata.fetchUser.user.mailingAddress?.city || undefined,
              province:
                defaultdata.fetchUser.user.mailingAddress?.province ||
                undefined,
              streetName:
                defaultdata.fetchUser.user.mailingAddress?.streetName ||
                undefined,
              postal:
                defaultdata.fetchUser.user.mailingAddress?.postal || undefined,
              unitNumber:
                defaultdata.fetchUser.user.mailingAddress?.unitNumber ||
                undefined,
              houseNumber:
                defaultdata.fetchUser.user.mailingAddress?.houseNumber ||
                undefined,
              neighborhood:
                defaultdata.fetchUser.user.mailingAddress?.neighborhood ||
                undefined,
              country:
                defaultdata.fetchUser.user.mailingAddress?.country || undefined,
            }
          : {},
        alternateAddress: defaultdata.fetchUser.user.alternateAddress
          ? {
              line1:
                defaultdata.fetchUser.user.alternateAddress?.line1 || undefined,
              line2:
                defaultdata.fetchUser.user.alternateAddress?.line2 || undefined,
              city:
                defaultdata.fetchUser.user.alternateAddress?.city || undefined,
              country:
                defaultdata.fetchUser.user.alternateAddress?.country ||
                undefined,
              postal:
                defaultdata.fetchUser.user.alternateAddress?.postal ||
                undefined,
              jurisdiction:
                defaultdata.fetchUser.user.alternateAddress?.jurisdiction ||
                undefined,
            }
          : {},
        customFields: defaultdata.fetchUser?.user?.customFields || [],
      });
      setApplicableJurisdictions(
        defaultdata.fetchUser.user.organization.jurisdictions,
      );
      setHasMailingAddress(
        Boolean(defaultdata.fetchUser.user.mailingAddress) &&
          !isEmpty(defaultdata.fetchUser.user.mailingAddress),
      );
      setHasAlternateAddress(
        Boolean(defaultdata.fetchUser.user.alternateAddress) &&
          !isEmpty(defaultdata.fetchUser.user.alternateAddress),
      );
    }
  }, [data, defaultdata, options, defaultTypeOfTrust]);

  useEffect(() => {
    if (!options?.typeOfTrust?.enabled) return;

    // Type of Trust: when enabled and mandatory, set default value to COMPLEX
    if (
      userData.type === EntityTypes.TRUST &&
      userData.typeOfTrust === '' &&
      options?.typeOfTrust?.required === FieldRequiredType.ERROR
    ) {
      setUserData({ ...userData, typeOfTrust: TrustTypes.COMPLEX });
    }

    // Type of Trust: unset for all entity types other than Trust
    if (userData.type !== EntityTypes.TRUST && userData.typeOfTrust !== '') {
      setUserData({ ...userData, typeOfTrust: '' });
    }
  }, [userData.type]);

  return (
    <NonIndividualInformationVisual
      options={options}
      userData={userData}
      updateUser={setUserData}
      continueFunc={updateUser}
      loading={
        loading || updateUserLoading || stepLoading || customFieldLoading
      }
      grid={grid}
      updateMode={updateMode}
      activeCustomFields={derivedCustomFieldsKeys}
      derivedCustomFields={derivedCustomFields}
      applicableJurisdictions={applicableJurisdictions}
      workflowCompletion={workflowCompletion}
      hasMailingAddress={hasMailingAddress}
      hasAlternateAddress={hasAlternateAddress}
      setHasMailingAddress={setHasMailingAddress}
      setHasAlternateAddress={setHasAlternateAddress}
    />
  );
};

export default NonIndividualInformation;
